/**
 * Landing header sticky Add-to-Cart bar.
 *
 * Behaviour
 *   • Shows a fixed bottom bar with product image, title, price and a big
 *     Add-to-cart button once the merchant's "Visual blocks" section has
 *     been scrolled fully out of view (its bottom edge above the viewport
 *     top).
 *   • If no `.visual-blocks` section is on the page, falls back to showing
 *     the bar as soon as the section's own Add-to-cart button leaves the
 *     viewport.
 *   • Clicking the sticky button triggers the real ATC button so the
 *     theme's product-form JS handles AJAX add, loader, notifications.
 *   • Mirrors the main button's disabled state, label text and the
 *     product price / compare-at price so variant swaps stay in sync.
 *   • The bar's variant row is wired up separately by
 *     src/scripts/sticky-variants.js, which the product page shares.
 *
 * Life-cycle
 *   • Init on DOMContentLoaded and on `shopify:section:load`.
 *   • Fully torn down on `shopify:section:unload` (no dangling scroll /
 *     resize listeners or MutationObservers).
 */

const SECTION_SELECTOR = '.js-landing-header';

class LandingHeaderSticky {
    constructor(root) {
        this.root = root;
        this.unique = root.dataset.unique || root.dataset.sectionId;
        this.stickyBar = document.getElementById(`LandingStickyATC--${this.unique}`);
        this.mainBtn = document.getElementById(`AddToCart--${this.unique}`);

        if (!this.stickyBar || !this.mainBtn) return;

        this.stickyBtn = this.stickyBar.querySelector('[data-add-to-cart]');
        this.mainText = this.mainBtn.querySelector('[data-add-to-cart-text]');
        this.stickyText = this.stickyBtn && this.stickyBtn.querySelector('[data-add-to-cart-text]');
        this.mainPriceWrap = root.querySelector('.product__price');
        this.mainPrice = this.mainPriceWrap && this.mainPriceWrap.querySelector('[data-product-price]');
        this.mainCompare = this.mainPriceWrap && this.mainPriceWrap.querySelector('[data-compare-price]');
        this.stickyPrice = this.stickyBar.querySelector('[data-product-price]');
        this.stickyCompare = this.stickyBar.querySelector('[data-compare-price]');

        this.observers = [];
        this.rafId = null;
        this.ticking = false;
        this.retryTimer = null;
        this.visualBlocks = null;
        this.viewportObserver = null;

        this.check = this.check.bind(this);
        this.onScroll = this.onScroll.bind(this);
        this.onStickyClick = this.onStickyClick.bind(this);
        this.syncFromMain = this.syncFromMain.bind(this);
        this.measure = this.measure.bind(this);

        window.addEventListener('resize', this.measure);

        this.bindClick();
        this.bindMainSync();
        this.syncFromMain();
        this.bindVisibility();
    }

    // The bar's height varies with the variant row (absent for single-variant
    // products, two rows when a product has both a colour and a size option),
    // so publish the measured height instead of hard-coding a body padding.
    measure() {
        const height = this.stickyBar.offsetHeight;
        if (height) {
            document.body.style.setProperty('--landing-sticky-atc-height', `${height}px`);
        }
    }

    show() {
        this.stickyBar.classList.add('landing-sticky-atc--visible');
        this.stickyBar.setAttribute('aria-hidden', 'false');
        this.measure();
        document.body.classList.add('has-landing-sticky-atc');
    }

    hide() {
        this.stickyBar.classList.remove('landing-sticky-atc--visible');
        this.stickyBar.setAttribute('aria-hidden', 'true');
        document.body.classList.remove('has-landing-sticky-atc');
    }

    // ------- Click delegation ------------------------------------------------
    bindClick() {
        if (!this.stickyBtn) return;
        this.stickyBtn.addEventListener('click', this.onStickyClick);
    }

    onStickyClick(e) {
        e.preventDefault();
        this.mainBtn.click();
    }

    // ------- Main → sticky sync ---------------------------------------------
    bindMainSync() {
        // Disabled state + button label
        const btnObserver = new MutationObserver(this.syncFromMain);
        btnObserver.observe(this.mainBtn, {
            attributes: true,
            attributeFilter: ['disabled'],
            childList: true,
            subtree: true,
            characterData: true,
        });
        this.observers.push(btnObserver);

        // Price + compare price (theme's variant JS re-renders these and toggles
        // the `hidden` class on the compare price when a variant is not on sale)
        [this.mainPrice, this.mainCompare].forEach((el) => {
            if (!el) return;
            const o = new MutationObserver(this.syncFromMain);
            o.observe(el, {
                attributes: true,
                attributeFilter: ['class'],
                childList: true,
                subtree: true,
                characterData: true,
            });
            this.observers.push(o);
        });
    }

    syncFromMain() {
        if (this.stickyBtn) this.stickyBtn.disabled = this.mainBtn.disabled;
        if (this.stickyText && this.mainText) {
            this.stickyText.textContent = this.mainText.textContent;
        }
        if (this.stickyPrice && this.mainPrice) {
            this.stickyPrice.innerHTML = this.mainPrice.innerHTML;
        }
        if (this.stickyCompare && this.mainCompare) {
            this.stickyCompare.innerHTML = this.mainCompare.innerHTML;
            this.stickyCompare.classList.toggle(
                'hidden',
                this.mainCompare.classList.contains('hidden') || !this.mainCompare.textContent.trim()
            );
        }
    }

    // ------- Visibility trigger ---------------------------------------------
    bindVisibility() {
        const vb = document.querySelector('.js-visual-blocks, .visual-blocks');
        if (vb) {
            this.attachScrollTrigger(vb);
            return;
        }

        // Retry a few times (async block rendering / theme editor)
        let tries = 0;
        this.retryTimer = window.setInterval(() => {
            const el = document.querySelector('.js-visual-blocks, .visual-blocks');
            if (el) {
                window.clearInterval(this.retryTimer);
                this.retryTimer = null;
                this.attachScrollTrigger(el);
                return;
            }
            if (++tries > 20) { // ~5s
                window.clearInterval(this.retryTimer);
                this.retryTimer = null;
                this.attachButtonFallback();
            }
        }, 250);
    }

    attachScrollTrigger(visualBlocks) {
        this.visualBlocks = visualBlocks;
        window.addEventListener('scroll', this.onScroll, { passive: true });
        window.addEventListener('resize', this.onScroll);
        // Initial state after layout settles
        this.rafId = window.requestAnimationFrame(this.check);
    }

    attachButtonFallback() {
        // Show sticky as soon as the main ATC button leaves the viewport upward.
        this.viewportObserver = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    this.hide();
                } else if (entry.boundingClientRect.top < 0) {
                    this.show();
                }
            });
        }, { threshold: 0 });
        this.viewportObserver.observe(this.mainBtn);
    }

    onScroll() {
        if (this.ticking) return;
        this.ticking = true;
        this.rafId = window.requestAnimationFrame(this.check);
    }

    check() {
        this.ticking = false;
        this.rafId = null;
        if (!this.visualBlocks) return;
        const rect = this.visualBlocks.getBoundingClientRect();
        if (rect.bottom <= 0) {
            this.show();
        } else {
            this.hide();
        }
    }

    // ------- Cleanup ---------------------------------------------------------
    destroy() {
        if (this.stickyBtn) this.stickyBtn.removeEventListener('click', this.onStickyClick);
        window.removeEventListener('scroll', this.onScroll);
        window.removeEventListener('resize', this.onScroll);
        window.removeEventListener('resize', this.measure);
        document.body.style.removeProperty('--landing-sticky-atc-height');

        if (this.rafId) window.cancelAnimationFrame(this.rafId);
        if (this.retryTimer) window.clearInterval(this.retryTimer);
        if (this.viewportObserver) this.viewportObserver.disconnect();
        this.observers.forEach((o) => o.disconnect());
        this.observers = [];

        document.body.classList.remove('has-landing-sticky-atc');
    }
}

function init(scope = document) {
    scope.querySelectorAll(SECTION_SELECTOR).forEach((el) => {
        if (el._landingHeaderSticky) return;
        el._landingHeaderSticky = new LandingHeaderSticky(el);
    });
}

function destroyIn(scope) {
    if (!scope || !scope.querySelectorAll) return;
    scope.querySelectorAll(SECTION_SELECTOR).forEach((el) => {
        if (el._landingHeaderSticky) {
            el._landingHeaderSticky.destroy();
            el._landingHeaderSticky = null;
        }
    });
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
} else {
    init();
}

document.addEventListener('shopify:section:load', (event) => {
    if (event.target) init(event.target);
});

document.addEventListener('shopify:section:unload', (event) => {
    if (event.target) destroyIn(event.target);
});

